import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type PropsWithChildren,
} from 'react';

import {
  buildGoogleAuthUrl,
  createPkceChallenge,
  createPkceVerifier,
  extractAuthCodeFromUrl,
  GOOGLE_OAUTH_CONFIG,
} from '../config/googleAuth';
import { authService, toAuthTokens } from '../modules/Auth/services/authService';
import type { GoogleAuthResult, OAuthContextType } from './oauthTypes';

const OAUTH_MESSAGE_TYPE = 'budget-google-oauth';
const OAUTH_CHANNEL_NAME = 'budget-google-oauth';
/** Give postMessage / BroadcastChannel time to land before treating close as cancel. */
const CLOSE_CANCEL_GRACE_MS = 800;

type OAuthPayload = {
  type: typeof OAUTH_MESSAGE_TYPE;
  code: string | null;
  error: string | null;
};

type WebWindow = {
  location: { href: string; origin: string; pathname: string; search: string };
  opener: (WebWindow & { closed: boolean }) | null;
  closed: boolean;
  postMessage: (message: unknown, targetOrigin: string) => void;
  close: () => void;
  open: (url: string, target: string, features: string) => WebWindow | null;
  addEventListener: (type: string, listener: (event: MessageEventLike) => void) => void;
  removeEventListener: (type: string, listener: (event: MessageEventLike) => void) => void;
  setInterval: (handler: () => void, timeout: number) => number;
  setTimeout: (handler: () => void, timeout: number) => number;
  clearInterval: (id: number) => void;
  clearTimeout: (id: number) => void;
  history: { replaceState: (data: unknown, unused: string, url?: string) => void };
};

type MessageEventLike = {
  origin: string;
  data: Partial<OAuthPayload> | null;
};

type OAuthBroadcastChannel = {
  postMessage: (message: OAuthPayload) => void;
  close: () => void;
  onmessage: ((event: { data: unknown }) => void) | null;
};

type BroadcastChannelCtor = new (name: string) => OAuthBroadcastChannel;

const getWindow = (): WebWindow => globalThis as unknown as WebWindow;

const getBroadcastChannelCtor = (): BroadcastChannelCtor | null => {
  const ctor = (globalThis as { BroadcastChannel?: BroadcastChannelCtor }).BroadcastChannel;
  return ctor ?? null;
};

const OAuthContext = createContext<OAuthContextType | null>(null);

export function useOAuth(): OAuthContextType {
  const context = useContext(OAuthContext);
  if (!context) {
    throw new Error('useOAuth must be used within an OAuthProvider');
  }
  return context;
}

function isOAuthCallbackPath(): boolean {
  const { pathname } = getWindow().location;
  return pathname === '/oauth' || pathname.endsWith('/oauth');
}

function createOAuthChannel(): OAuthBroadcastChannel | null {
  const BroadcastChannel = getBroadcastChannelCtor();
  if (!BroadcastChannel) return null;
  try {
    return new BroadcastChannel(OAUTH_CHANNEL_NAME);
  } catch {
    return null;
  }
}

function publishOAuthPayload(payload: OAuthPayload): void {
  const win = getWindow();
  const channel = createOAuthChannel();
  channel?.postMessage(payload);
  channel?.close();

  // Fallback when BroadcastChannel is unavailable; may fail if COOP cleared opener.
  try {
    if (win.opener && !win.opener.closed) {
      win.opener.postMessage(payload, win.location.origin);
    }
  } catch {
    // Opener access can throw under COOP — channel relay is enough.
  }
}

/**
 * If this window is the OAuth redirect target (popup), forward the auth code
 * to the opener via BroadcastChannel (+ postMessage fallback).
 */
function relayOAuthCallbackIfNeeded(): boolean {
  if (!isOAuthCallbackPath()) {
    return false;
  }

  const win = getWindow();
  const code = extractAuthCodeFromUrl(win.location.href);
  const error = new URLSearchParams(win.location.search).get('error');
  const payload: OAuthPayload = {
    type: OAUTH_MESSAGE_TYPE,
    code,
    error: error || (code ? null : 'missing_code'),
  };

  if (!code && !error) {
    return false;
  }

  publishOAuthPayload(payload);
  // Defer close so the parent can receive the message before the browsing context dies.
  win.setTimeout(() => {
    try {
      win.close();
    } catch {
      // ignore
    }
  }, 50);
  return true;
}

function isOAuthPayload(data: unknown): data is OAuthPayload {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as OAuthPayload).type === OAUTH_MESSAGE_TYPE
  );
}

/**
 * Google’s COOP can make `popup.closed === true` while accounts.google.com is
 * still open. Treat cross-origin access errors as “still open”.
 */
function isPopupDefinitelyClosed(popup: WebWindow): boolean {
  try {
    if (!popup.closed) return false;
  } catch {
    return false;
  }

  try {
    // Readable only same-origin (our /oauth page or about:blank after close).
    void popup.location.href;
    return popup.closed;
  } catch {
    // Cross-origin (Google) — popup is still open despite closed===true.
    return false;
  }
}

export function OAuthProvider({ children }: PropsWithChildren) {
  const isAuthenticatingRef = useRef(false);
  const relayedRef = useRef(false);

  useEffect(() => {
    if (relayedRef.current) return;
    relayedRef.current = true;
    relayOAuthCallbackIfNeeded();
  }, []);

  const finishWithBackendCode = useCallback(
    async (params: {
      code: string;
      redirectUri: string;
      codeVerifier: string;
    }): Promise<GoogleAuthResult> => {
      // Server holds client_secret — browser must not exchange the code.
      const response = await authService.googleAuthWithCode(params);
      const tokens = toAuthTokens(response);
      return {
        user: response.user,
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        isNewUser: response.isNewUser,
      };
    },
    [],
  );

  const authenticate = useCallback(async (): Promise<GoogleAuthResult> => {
    if (isAuthenticatingRef.current) {
      throw new Error('Authentication already in progress');
    }

    if (!GOOGLE_OAUTH_CONFIG.clientId) {
      throw new Error(
        'Google web client ID is not configured. Set BUDGET_GOOGLE_CLIENT_ID_WEB in packages/budget/.env and restart yarn web (see docs/AUTH_SETUP.md).',
      );
    }

    const win = getWindow();
    const redirectUri = GOOGLE_OAUTH_CONFIG.redirectUri;
    const state = Math.random().toString(36).substring(2, 15);
    const codeVerifier = createPkceVerifier();
    const codeChallenge = await createPkceChallenge(codeVerifier);
    const authUrl = await buildGoogleAuthUrl(state, redirectUri, { challenge: codeChallenge });

    isAuthenticatingRef.current = true;

    try {
      if (isOAuthCallbackPath()) {
        const existingCode = extractAuthCodeFromUrl(win.location.href);
        if (existingCode) {
          win.history.replaceState({}, '', '/');
          return finishWithBackendCode({ code: existingCode, redirectUri, codeVerifier });
        }
      }

      const popup = win.open(
        authUrl,
        'budget-google-oauth',
        'width=480,height=720,menubar=no,toolbar=no',
      );

      if (!popup) {
        throw new Error(
          'Pop-up blocked. Allow pop-ups for this site and try Continue with Google again.',
        );
      }

      const result = await new Promise<GoogleAuthResult>((resolve, reject) => {
        let settled = false;
        /** True once we have a code/error — stop close-poll cancel during backend exchange. */
        let handling = false;
        let cancelTimer: number | null = null;
        let closePoll: number | null = null;
        const channel = createOAuthChannel();

        const cleanup = () => {
          win.removeEventListener('message', onWindowMessage);
          channel?.close();
          if (closePoll != null) win.clearInterval(closePoll);
          if (cancelTimer != null) win.clearTimeout(cancelTimer);
        };

        const settleReject = (error: Error) => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(error);
        };

        const settleResolve = (value: GoogleAuthResult) => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve(value);
        };

        const handlePayload = (data: OAuthPayload) => {
          if (settled || handling) return;
          handling = true;
          if (cancelTimer != null) {
            win.clearTimeout(cancelTimer);
            cancelTimer = null;
          }
          if (closePoll != null) {
            win.clearInterval(closePoll);
            closePoll = null;
          }

          if (data.error || !data.code) {
            settleReject(new Error(data.error || 'Google authentication failed'));
            return;
          }

          finishWithBackendCode({ code: data.code, redirectUri, codeVerifier })
            .then(settleResolve)
            .catch((err) => settleReject(err as Error));
        };

        const onWindowMessage = (event: MessageEventLike) => {
          if (event.origin !== win.location.origin) return;
          if (!isOAuthPayload(event.data)) return;
          handlePayload(event.data);
        };

        win.addEventListener('message', onWindowMessage);
        if (channel) {
          channel.onmessage = (event) => {
            if (!isOAuthPayload(event.data)) return;
            handlePayload(event.data);
          };
        }

        closePoll = win.setInterval(() => {
          if (settled || handling) return;

          if (!isPopupDefinitelyClosed(popup)) {
            if (cancelTimer != null) {
              win.clearTimeout(cancelTimer);
              cancelTimer = null;
            }
            return;
          }

          // Grace period: popup often closes right as BroadcastChannel/postMessage arrives.
          if (cancelTimer == null) {
            cancelTimer = win.setTimeout(() => {
              if (!settled && !handling) {
                settleReject(new Error('Google authentication was cancelled'));
              }
            }, CLOSE_CANCEL_GRACE_MS);
          }
        }, 400);
      });

      return result;
    } finally {
      isAuthenticatingRef.current = false;
    }
  }, [finishWithBackendCode]);

  return (
    <OAuthContext.Provider
      value={{
        authenticate,
        isAuthenticating: isAuthenticatingRef.current,
      }}
    >
      {children}
    </OAuthContext.Provider>
  );
}
